import { useMemo, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'

interface Screen {
  image: string
  title: string
  subtitle: string
}

const DEFAULT_SCREENS: Screen[] = [
  {
    image: '/images/home.png',
    title: 'Track Your Nutrients',
    subtitle: 'Monitor sodium, potassium, phosphorus & protein daily',
  },
  {
    image: '/images/photoscan.png',
    title: 'AI Meal Scanner',
    subtitle: 'Snap a photo to instantly check any meal',
  },
  {
    image: '/images/smartswaps.png',
    title: 'Smart Swaps',
    subtitle: 'Get kidney-friendly alternatives for any meal',
  },
  {
    image: '/images/recipes.png',
    title: 'Kidney-Safe Recipes',
    subtitle: '100+ recipes filtered for your CKD stage',
  },
  {
    image: '/images/recipesingle.png',
    title: 'Recipe Details',
    subtitle: 'Full nutrition breakdown for every recipe',
  },
  {
    image: '/images/recipesimport.png',
    title: 'Import Any Recipe',
    subtitle: 'Analyze recipes from URLs or cookbook photos',
  },
  {
    image: '/images/progress.png',
    title: 'Nutrient Trends',
    subtitle: 'See patterns and track progress over time',
  },
  {
    image: '/images/fluids.png',
    title: 'Fluid Tracking',
    subtitle: 'Monitor your daily fluid intake with ease',
  },
]

// organic-v2: current app (4.41, captured 2026-10-01 from the QA account with demo data).
export const V2_SCREENS: Screen[] = [
  {
    image: '/images/v2/screens/1-diary.webp',
    title: 'Your Daily Kidney Budget',
    subtitle: 'Sodium, potassium, phosphorus & protein against your limits',
  },
  {
    image: '/images/v2/screens/2-scan.webp',
    title: 'AI Meal Scanner',
    subtitle: 'Snap a photo for an instant kidney score',
  },
  {
    image: '/images/v2/screens/3-menu.webp',
    title: 'Restaurant Menu Picks',
    subtitle: 'Scan a menu and see the best choices for your kidneys',
  },
  {
    image: '/images/v2/screens/4-recipes.webp',
    title: 'Kidney-Friendly Recipes',
    subtitle: '100+ recipes rated for your profile',
  },
  {
    image: '/images/v2/screens/5-recipe-detail.webp',
    title: 'Every Nutrient, Per Serving',
    subtitle: 'Full breakdown, and why each recipe scores the way it does',
  },
  {
    image: '/images/v2/screens/6-labs.webp',
    title: 'Track Your Labs',
    subtitle: 'eGFR, creatinine, potassium & more over time',
  },
  {
    image: '/images/v2/screens/7-progress.webp',
    title: 'See Your Progress',
    subtitle: 'A day-by-day calendar and nutrient trends',
  },
  {
    image: '/images/v2/screens/8-report.webp',
    title: "Doctor's Report",
    subtitle: 'A PDF summary for your next nephrology visit',
  },
]

// Create many copies so it feels infinite (no loop mode needed)
const COPIES = 20

export function AppScreensCarousel({ screens = DEFAULT_SCREENS }: { screens?: Screen[] }) {
  const allSlides = useMemo(() => Array.from({ length: COPIES }, () => screens).flat(), [screens])
  // Start in the middle so user can swipe both directions
  const MIDDLE_START = Math.floor(COPIES / 2) * screens.length
  const [activeIndex, setActiveIndex] = useState(0)
  const swiperRef = useRef<SwiperType | null>(null)

  return (
    <div className="py-8">
      <h3 className="text-xl font-bold text-center text-gray-900 mb-2">
        Get your best results with KidneyPal
      </h3>
      <p className="text-sm text-gray-500 text-center mb-6">
        Everything you need to manage your kidney diet
      </p>

      <div className="carousel-wrapper">
        <Swiper
          slidesPerView={1.8}
          centeredSlides
          spaceBetween={8}
          initialSlide={MIDDLE_START}
          grabCursor
          onSwiper={(swiper) => {
            swiperRef.current = swiper
            setActiveIndex(swiper.activeIndex % screens.length)
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex % screens.length)
          }}
        >
          {allSlides.map((screen, i) => (
            <SwiperSlide key={i}>
              {({ isActive }: { isActive: boolean }) => (
                <div
                  className="transition-all duration-300 ease-out mx-auto"
                  style={{
                    transform: isActive ? 'scale(1)' : 'scale(0.85)',
                    opacity: isActive ? 1 : 0.5,
                    maxWidth: '220px',
                  }}
                >
                  <img
                    src={screen.image}
                    alt={screen.title}
                    className="w-full h-auto pointer-events-none"
                    draggable={false}
                  />
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-4">
        {screens.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              if (!swiperRef.current) return
              const current = swiperRef.current.activeIndex
              const currentReal = current % screens.length
              const diff = i - currentReal
              swiperRef.current.slideTo(current + diff)
            }}
            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
              i === activeIndex ? 'bg-primary' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Caption */}
      <div className="text-center mt-3">
        <p className="font-semibold text-gray-900">{screens[activeIndex].title}</p>
        <p className="text-sm text-gray-500 mt-1">{screens[activeIndex].subtitle}</p>
      </div>
    </div>
  )
}
